import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {getCardById} from 'stores/selectors/cardStateSelector';

import Spinner from '../../../../components/Spinner/Spinner';
import TaskNumber from '../../Components/TaskNumber';
import withIntl from '../../../../lib/withIntl';
import {useArrowDownPress, useArrowUpPress, useDispatchAndAddUntoItem} from '../../../../lib/customHooks';

import {ExecutionStatus} from '../../../../constants/dataEnum';

import './TaskAirGap.scss';
import {getAirGapLabwareID, onEditSimpleTransferHandler} from './SimpleTransfer/Tasks/simpleTransferTaskBusiness';
import TaskAction from './Components/TaskAction';

function TaskAirGapView({
  volume,
  ID,
  number,
  isIgnored,
  onCheckedChange,
  onEdit,
  onDelete,
  runtimeMode,
  executionStatus,
  isDragging,
  isHighlightedTask,
  intl,
  onArrowUpPress,
  onArrowDownPress,
  isViewMode,
}) {
  const taskRef = useRef();

  useArrowUpPress(taskRef, onArrowUpPress);
  useArrowDownPress(taskRef, onArrowDownPress);

  return (
    <div
      ref={taskRef}
      className={`task-airGap-view ${isDragging && 'dragging'} ${isHighlightedTask && 'selected'}`}
      role="presentation"
    >
      <TaskNumber number={number} isHighlighted={isHighlightedTask} />
      <span className="task-airGap-view-content">
        <span className="task-airGap-view-icon">
          <img src="./svg/protocol-editor/airgap-blue.svg" alt="Air Gap" />
        </span>
        {intl.formatMessage({id: 'protocolEditor.taskList.taskAirGap.contentTitle'})}
      </span>
      {!runtimeMode && !isViewMode && (
        <TaskAction
          onEdit={onEdit}
          onDelete={(e) => {
            onDelete(ID);
            e?.stopPropagation();
          }}
          isIgnored={isIgnored}
          onIgnoreCheckboxChanged={(value) => onCheckedChange(ID, value)}
        />
      )}
      <div className="task-airGap-view-message">
        <span>{intl.formatMessage({id: 'protocolEditor.taskList.taskAirGap.messageTitle'})}</span>
        <span className="task-airGap-view-volume">{volume}</span>
        <span>{intl.formatMessage({id: 'protocolEditor.taskList.taskAirGap.volume'})}</span>
      </div>
      <div className="task-airGap-view-message">
        {!runtimeMode && (
          <div
            className="task-reuse-tips"
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        )}
      </div>
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && (
        <img className="executed" src="./svg/protocol-editor/execution-done.svg" alt="Executed" />
      )}
    </div>
  );
}

TaskAirGapView.propTypes = {
  volume: PropTypes.string.isRequired,
  ID: PropTypes.number.isRequired,
  number: PropTypes.number,
  isHighlightedTask: PropTypes.bool.isRequired,
  isIgnored: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  runtimeMode: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  isDragging: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool.isRequired,
};

TaskAirGapView.defaultProps = {
  number: undefined,
};

function TaskAirGap({
  cardId,
  task,
  onCheckedChange,
  onDelete,
  onSelectTask,
  runtimeMode,
  executionStatus,
  intl,
  onDrag,
  isDragging,
  onArrowUpPress,
  onArrowDownPress,
  readOnly,
}) {
  const dispatch = useDispatch();

  const {ID, number, isIgnored, isSelected} = task;
  const selectedCard = useSelector(getCardById(cardId));

  const dispatchAddUndo = useDispatchAndAddUntoItem();

  const onEdit = () => {
    const labwareID = dispatch(getAirGapLabwareID(ID, selectedCard));
    const callback = () => onSelectTask(null, {bypassValidityChecking: true});
    dispatchAddUndo(onEditSimpleTransferHandler({...task, labwareID}, selectedCard.ID, callback));
  };

  return (
    <div className="wrapper" ref={(el) => onDrag && onDrag(el)}>
      <TaskAirGapView
        ID={ID}
        task={task}
        volume={task.volume}
        onEdit={() => onEdit(ID)}
        runtimeMode={runtimeMode}
        executionStatus={executionStatus}
        number={number}
        onCheckedChange={onCheckedChange}
        onDelete={onDelete}
        drag={onDrag}
        isDragging={isDragging}
        isIgnored={isIgnored}
        isHighlightedTask={isSelected}
        intl={intl}
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
        isViewMode={readOnly}
      />
    </div>
  );
}

TaskAirGap.propTypes = {
  cardId: PropTypes.string,
  task: PropTypes.object,
  onCheckedChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  runtimeMode: PropTypes.bool,
  executionStatus: PropTypes.string,
  onDrag: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
};

TaskAirGap.defaultProps = {
  cardId: null,
  task: {
    ID: 0,
    message: '',
  },
  runtimeMode: false,
  executionStatus: ExecutionStatus.NONE,
};

export default withIntl(TaskAirGap);
